import PalommaLogoSmall from "@/assets/PalommaLogoSmall.jpg";
import { faqSection, faqsFor } from "@/config/faq";
import { WHATSAPP_SUPPORT_URL } from "@/config/tutorials";
import { track } from "@/helpers/posthog";
import {
  Box,
  Heading,
  IconButton,
  Image,
  Input,
  Link,
  Text,
} from "@chakra-ui/react";
import { ChevronDown, ChevronRight, Search, X } from "lucide-react";
import { useMemo, useState } from "react";
import { FaWhatsapp } from "react-icons/fa";
import { Link as RouterLink, useLocation } from "react-router-dom";

const SupportWidget = (): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  const [hintDismissed, setHintDismissed] = useState(false);
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState<string>();
  const { pathname } = useLocation();

  const faqs = useMemo(() => faqsFor(pathname, query), [pathname, query]);
  const section = faqSection(pathname);

  const toggleFaq = (id: string): void => {
    const next = openId === id ? undefined : id;
    setOpenId(next);
    if (next) track("support_faq_opened", { id, path: pathname });
  };

  return (
    <Box position="relative">
      {isOpen && (
        <Box
          position="absolute"
          right={0}
          bottom="calc(100% + 12px)"
          zIndex={1000}
          width="372px"
          maxH="calc(100vh - 96px)"
          display="flex"
          flexDirection="column"
          borderRadius="18px"
          overflow="hidden"
          bg="#f6f5fa"
          boxShadow="0 24px 60px rgba(17,3,46,.28)"
          transformOrigin="bottom right"
          animation="support-pop .22s cubic-bezier(.2,.9,.3,1.1)"
        >
          <style>{`
            @keyframes support-pop {
              from { opacity: 0; transform: scale(.4) translateY(24px); }
              to { opacity: 1; transform: scale(1) translateY(0); }
            }
          `}</style>
          <Box
            flexShrink={0}
            bg="palomma.700"
            px={5}
            pt={4.5}
            pb="76px"
            color="white"
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Image
                src={PalommaLogoSmall}
                alt="Palomma"
                boxSize="36px"
                borderRadius="10px"
              />
              <IconButton
                aria-label="Cerrar ayuda"
                variant="ghost"
                color="white"
                _hover={{ bg: "whiteAlpha.200" }}
                onClick={() => setIsOpen(false)}
              >
                <X size={20} />
              </IconButton>
            </Box>
            <Heading mt={7} fontSize="28px" lineHeight="1.15" fontWeight={700}>
              Hola,
              <br />
              ¿cómo podemos ayudarte?
            </Heading>
          </Box>
          <Box
            mt="-52px"
            px={3.5}
            pb={3.5}
            overflowY="auto"
            display="flex"
            flexDirection="column"
            gap={2.5}
          >
            <Link
              href={WHATSAPP_SUPPORT_URL}
              target="_blank"
              rel="noopener"
              display="flex"
              alignItems="center"
              gap={3}
              bg="white"
              borderRadius="12px"
              px={4}
              py={3.5}
              boxShadow="0 2px 10px rgba(17,3,46,.08)"
              textDecoration="none"
              _hover={{ boxShadow: "0 6px 18px rgba(17,3,46,.14)" }}
              onClick={() =>
                track("support_whatsapp_clicked", { path: pathname })
              }
            >
              <Box flex={1}>
                <Text fontSize="15px" fontWeight={700} color="gray.900">
                  Escríbenos por WhatsApp
                </Text>
                <Text fontSize="13px" color="gray.600" mt={0.5}>
                  Nuestro equipo de soporte te responde
                </Text>
              </Box>
              <Box
                boxSize="38px"
                borderRadius="full"
                bg="#25D366"
                color="white"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <FaWhatsapp size={22} />
              </Box>
            </Link>
            <Box
              bg="white"
              borderRadius="12px"
              p={2.5}
              boxShadow="0 2px 10px rgba(17,3,46,.08)"
            >
              <Box position="relative">
                <Input
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setOpenId(undefined);
                  }}
                  placeholder="Buscar ayuda"
                  bg="#f0eff5"
                  border="none"
                  borderRadius="10px"
                  fontSize="14px"
                  pr={10}
                />
                <Box
                  position="absolute"
                  right={3}
                  top="50%"
                  transform="translateY(-50%)"
                  color="gray.600"
                  pointerEvents="none"
                >
                  <Search size={18} />
                </Box>
              </Box>
              <Text fontSize="12px" color="gray.500" mt={3} mb={1} px={1.5}>
                {section && !query.trim()
                  ? `Preguntas frecuentes · ${section}`
                  : "Preguntas frecuentes"}
              </Text>
              {faqs.length === 0 && (
                <Text fontSize="13px" color="gray.600" px={1.5} py={2}>
                  No encontramos nada sobre eso. Escríbenos por WhatsApp y te
                  ayudamos.
                </Text>
              )}
              {faqs.map((faq) => {
                const expanded = openId === faq.id;
                return (
                  <Box
                    key={faq.id}
                    borderTop="1px solid #ebecef"
                    _first={{ borderTop: "none" }}
                  >
                    <Box
                      as="button"
                      width="100%"
                      display="flex"
                      alignItems="center"
                      gap={2}
                      textAlign="left"
                      px={1.5}
                      py={2.5}
                      fontSize="14px"
                      color="gray.900"
                      cursor="pointer"
                      _hover={{ color: "palomma.700" }}
                      onClick={() => toggleFaq(faq.id)}
                    >
                      <Text flex={1}>{faq.q}</Text>
                      <Box
                        color="gray.500"
                        transform={expanded ? "rotate(90deg)" : "none"}
                        transition="transform .15s ease"
                      >
                        <ChevronRight size={16} />
                      </Box>
                    </Box>
                    {expanded && (
                      <Box px={1.5} pb={3}>
                        <Text fontSize="13px" color="gray.600" lineHeight="1.5">
                          {faq.a}
                        </Text>
                        {faq.cta && (
                          <Link
                            asChild
                            fontSize="13px"
                            fontWeight={600}
                            color="palomma.700"
                            mt={1.5}
                          >
                            <RouterLink
                              to={faq.cta.to}
                              onClick={() => setIsOpen(false)}
                            >
                              {faq.cta.label}
                            </RouterLink>
                          </Link>
                        )}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      )}
      {!isOpen && !hintDismissed && (
        <Box
          position="absolute"
          right={0}
          bottom="calc(100% + 10px)"
          zIndex={1000}
          whiteSpace="nowrap"
          bg="white"
          border="1px solid #ebecef"
          borderRadius="10px"
          boxShadow="0 6px 18px rgba(17,24,39,.12)"
          py={1.5}
          pl={3}
          pr={7}
        >
          <Text fontSize="13px" fontWeight={600} color="gray.900">
            ¿Necesitas ayuda?
          </Text>
          <Text fontSize="12px" color="gray.600">
            Escríbenos por WhatsApp
          </Text>
          <IconButton
            aria-label="Ocultar mensaje"
            variant="ghost"
            size="2xs"
            position="absolute"
            top={1}
            right={1}
            color="gray.500"
            onClick={() => setHintDismissed(true)}
          >
            <X size={12} />
          </IconButton>
        </Box>
      )}
      <IconButton
        aria-label={isOpen ? "Cerrar ayuda" : "Abrir ayuda"}
        boxSize="44px"
        borderRadius="full"
        color="white"
        bg={isOpen ? "palomma.700" : "#25D366"}
        boxShadow={
          isOpen
            ? "0 8px 24px rgba(17,3,46,.35)"
            : "0 6px 20px rgba(37,211,102,.35)"
        }
        _hover={{ transform: "scale(1.06)" }}
        transition="transform .15s ease, background .25s ease, box-shadow .25s ease"
        onClick={() => setIsOpen(!isOpen)}
      >
        <Box
          position="absolute"
          display="flex"
          opacity={isOpen ? 0 : 1}
          transform={isOpen ? "rotate(90deg) scale(.4)" : "none"}
          transition="opacity .2s ease, transform .25s ease"
          css={{ "& svg": { width: "26px", height: "26px" } }}
        >
          <FaWhatsapp />
        </Box>
        <Box
          position="absolute"
          display="flex"
          opacity={isOpen ? 1 : 0}
          transform={isOpen ? "none" : "rotate(-90deg) scale(.4)"}
          transition="opacity .2s ease, transform .25s ease"
          css={{ "& svg": { width: "20px", height: "20px" } }}
        >
          <ChevronDown />
        </Box>
      </IconButton>
    </Box>
  );
};

export default SupportWidget;
